// The Fleet page's ONE move controller (#6771, epic #6552): a drag crosses cards, so what is
// being moved, the server's preview for it, the drop target under the pointer and the
// announcements live here, once per page, made by `FleetMachines` beside the page's flood
// guard and handed to every card through {@link MovePageContext}.
//
// ONE PREVIEW PER DRAG OR MENU. A drag starting or Move to opening makes ONE `POST
// /v1/runners/:id/moves/preview` (#6768) for its item, and every drop panel and every option
// renders from that answer; the browser computes no limit. Pending, everything reads
// "Checking…" and refuses a drop or a pick; failed, nothing starts and the failure is
// announced.
//
// A MOVE IS ONE SEND THROUGH THE PAGE GUARD (`./flood-guard.ts`), counted like every other
// card change, with no debounce (`delayMs: 0`; the prototype's doMove counts it the same
// way, tripLock `:1658`). The controller never makes a guard of its own. Its answer is read
// HERE and never handed to the card controls' failure handler: a 429 (`cooling_down`,
// `source_budget`, `target_budget`, `transfers_in_flight`) renders INLINE on its move with
// its wait counted down, and does not lock the page (a coordinator choice, #6771). Only the
// guard's own lock locks every control, moves included. Every send is followed by a re-read.
//
// #6772 adds, on the same seams: a MULTI-ITEM send (`moveMany`: the Move work panel, Undo and
// each Put-back part) through the same `guard.change(..., { delayMs: 0 })`, the same
// `readMoveRefusal` and the same re-read; the ACCEPTED-MOVE hook (`onMoveAccepted`), called on
// every 201 of either send BEFORE the re-read, so drag, Move to, the panel, Undo and Put back
// fire the notes and the transfer motion from one place; the panel's per-item previews
// (`previewItems`, which never touches the one session); each machine's moves budget as the
// newest answer reports it, laid over the row (`movesOf`, `./move-meter.ts`), which
// `blockedWhy` reads; and one deadline registry (`tickUntil`) that keeps the page clock ticking
// while anything on screen counts down to a time, and stops it once every deadline passed.

import {
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  untrack,
  useContext,
} from 'solid-js';
import { t } from '../i18n';
import { clientErrorReport, reportClientError } from './client-errors';
import type { UtilBox } from './fleetUtilization';
import { lockedSeconds, type PageGuard } from './flood-guard-context';
import { capabilityKey } from './machine-jobs';
import { type BudgetAnswer, createMoveBudgets, type MoveBudgets } from './move-budgets';
import {
  type MoveAccepted,
  type MoveItem,
  type MovePreview,
  type MovePreviewTarget,
  type MoveRefusalWire,
  previewMove,
  readMoveRefusal,
  sendMove,
} from './move-calls';
import { type MoveCopyContext, moveRefusalText, standingRefusal } from './move-copy';
import { gripDisabledWhy, type MoveSubject } from './move-items';
import { effectiveMoves, type MovesRow } from './move-meter';
import { type MoveRefused, notifyRefused, refuseSend } from './move-refused';
import { itemSignature, previewConfirms, previewReaskOnRead, reaskOnRead } from './move-work-panel';
import { orderStamp } from './read-order';

export type PreviewState =
  | { readonly status: 'pending' }
  | { readonly status: 'ready'; readonly preview: MovePreview }
  | { readonly status: 'failed' };

/** The one move in hand: what, how it was started, and the server's preview for it. */
export type MoveSession = {
  readonly subject: MoveSubject;
  readonly via: 'menu' | 'drag';
  readonly preview: PreviewState;
  /** When the ask that produced `preview` started (`orderStamp`): a refusal older is superseded. */
  readonly order: number;
  /** The fleet read's item it was asked for (`itemSignature`): another read re-asks it. */
  readonly sig: string;
  /** The fleet read it was asked on (`fleetRead`): a refusal no row field clears is re-asked on a later one. */
  readonly read: number;
};

/** A move's 429, shown inline on that move until its wait ends. */
export type MoveWait = {
  readonly refusal: MoveRefusalWire;
  /** The machine it was sent from, and what: a later fleet read re-asks it (`READ_REASKED`). */
  readonly sourceId: string;
  readonly items: readonly MoveItem[];
  /** The fleet read it arrived on (`fleetRead`). */
  readonly read: number;
  /** The machine it was sent to. */
  readonly targetId: string;
  /** When it stops showing, `Date.now` ms. */
  readonly until: number;
};

/** How a move ended. */
export type MoveOutcome =
  | { readonly kind: 'moved'; readonly accepted: MoveAccepted }
  | { readonly kind: 'refused'; readonly refusal: MoveRefusalWire; readonly text: string }
  | { readonly kind: 'wait'; readonly refusal: MoveRefusalWire; readonly text: string }
  | { readonly kind: 'failed' }
  /** The page guard held it: locked, or this item's move is still saving. */
  | { readonly kind: 'held' };

/** What one target says in this session: checking, or the preview's answer for it. */
export type TargetView =
  | { readonly kind: 'checking' }
  | {
      readonly kind: 'answer';
      readonly target: MovePreviewTarget;
      readonly why: string | null;
      /** The standing refusal `why` renders (the source's, else the target's); null with `why`. */
      readonly refusal: MoveRefusalWire | null;
    };

/** A pressed row waiting to become a drag (`components/machine-card/DragLayer.tsx`). */
export type MovePress = {
  readonly subject: MoveSubject;
  readonly row: HTMLElement;
  readonly event: PointerEvent;
};

/** One send of one or more items off one machine to another (#6772). */
export type MoveRequest = {
  readonly sourceId: string;
  readonly items: readonly MoveItem[];
  readonly targetId: string;
  /** The page guard's key and the inline wait's key: page-unique for this send. */
  readonly key: string;
  /** "The Coder job", "2 epics and 3 jobs": what the announcement names. */
  readonly what: string;
  /** The verb after `what` is plural ("are moving"). */
  readonly many: boolean;
  /** Now for job moves, Mission for epic moves: where the note shows. */
  readonly zone: 'now' | 'mission';
  /** An Undo: the batch it reverses (`undo_of`). */
  readonly undoOf?: string;
  /** A Put-back part: no note and no Undo of its own. */
  readonly putBack?: boolean;
  /** Said on the 201 in place of "… is moving to <m>" (Undo's "is moving back to"). */
  readonly announce?: string;
  /** The single item a drag or Move to sends (the transfer card's name, #6773). */
  readonly subject?: MoveSubject;
};

/** What the accepted-move hook is handed: the 201 and the request it answered. */
export type AcceptedMove = {
  readonly request: MoveRequest;
  readonly accepted: MoveAccepted;
};

export type { MoveRefused } from './move-refused';

export type MovePageDeps = {
  readonly guard: PageGuard;
  readonly token: () => string | null;
  /** The whole fleet read: names, specs and the jobs a refusal's `item` names. */
  readonly boxes: () => readonly UtilBox[];
  /** Re-read the fleet: every move is followed by one, whatever it answered. */
  readonly onWritten: () => void;
  /**
   * The accepted-move hook (#6772, `./accepted-move.ts`): called on every 201, drag, Move to,
   * the panel, Undo and Put back alike, before the re-read, while the moved rows are still on
   * their source card, with `order`, when that send STARTED (`orderStamp`, before the POST:
   * the transfer stack orders its cards by it, #6773). Absent, a 201 only announces.
   */
  readonly onMoveAccepted?: (a: AcceptedMove, order: number) => void;
  /**
   * The refusal hook (#6773): every send refused or failed (drag, Move to, the panel, Undo, each
   * Put-back part) and every drag refused at its source or on a drop. A send the page guard held,
   * or a drop the page's own pre-send check refused (`blockedWhy`), is not handed here.
   */
  readonly onMoveRefused?: (r: MoveRefused) => void;
  /**
   * Where an error thrown AFTER a 201 goes (the accepted-move hook's): reported on its own, never
   * turned into a failed move. Default: the dashboard's client error sink.
   */
  readonly reportError?: (err: unknown) => void;
  /**
   * When the fetch behind the shown fleet read started, in the page's order (`orderStamp`): a
   * budget answer newer in that order outranks the row. Never wall-clock ms, which ties.
   */
  readonly readOrder?: () => number;
  readonly previewer?: typeof previewMove;
  readonly mover?: typeof sendMove;
  /** A fresh `Idempotency-Key` per send. */
  readonly mintKey?: () => string;
};

const without = <K, V>(m: ReadonlyMap<K, V>, k: K): ReadonlyMap<K, V> => {
  if (!m.has(k)) return m;
  const next = new Map(m);
  next.delete(k);
  return next;
};

/** Session state: the one session, the press, the drag target and the waits. */
function createMoveState() {
  const [session, setSession] = createSignal<MoveSession | null>(null);
  const [pressed, setPressed] = createSignal<MovePress | null>(null);
  const [over, setOver] = createSignal<{ readonly id: string; readonly y: number } | null>(null);
  const [waits, setWaits] = createSignal<ReadonlyMap<string, MoveWait>>(new Map());
  const [live, setLive] = createSignal('');
  const [clock, setClock] = createSignal(Date.now());
  const [focusKey, setFocusKey] = createSignal<string | null>(null);
  const [flash, setFlash] = createSignal<string | null>(null);
  // The countdowns' ONE tick: an interval while any wait shows, the session's preview holds
  // a timed refusal ("Try again in N s", review thread on move-page.ts), OR a surface outside
  // the session registered a deadline still ahead (`tickUntil`, #6772: the panel's previews,
  // the meter, the rings, Undo); stopped when none does. Every sentence reads `clock`, so
  // nothing counts from the clock of an older read.
  let ticker: ReturnType<typeof setInterval> | undefined;
  /** The latest deadline any surface registered, `Date.now` ms. */
  let needUntil = 0;
  /** The session's preview holds a timed refusal that has not passed yet. */
  const timedPreview = (): boolean => {
    const p = session()?.preview;
    if (p === undefined || p.status !== 'ready') return false;
    const now = Date.now();
    const timed = (r: { readonly free_at?: string | null | undefined } | undefined): boolean =>
      r?.free_at != null && standingRefusal(r, now) !== null;
    return timed(p.preview.source.refusal) || p.preview.targets.some((x) => timed(x.refusal));
  };
  /** Something on screen still counts down at `now`. */
  const timed = (now: number): boolean =>
    untrack(() => waits().size > 0 || timedPreview() || needUntil > now);
  const tick = (): void => {
    const now = Date.now();
    setClock(now);
    const standing = [...untrack(waits)].filter(([, w]) => w.until > now);
    if (standing.length !== untrack(waits).size) setWaits(new Map(standing));
    // One tick past the last deadline, so a surface reads the clock after its time passed.
    if (!timed(now - 1_000) && ticker !== undefined) {
      clearInterval(ticker);
      ticker = undefined;
    }
  };
  /** The page left: no late answer may start a tick, set a wait or speak (see `onCleanup`). */
  let disposed = false;
  /** Read the clock now, and keep ticking while something on screen counts down. */
  const retick = (): void => {
    if (disposed) return;
    const now = Date.now();
    setClock(now);
    if (timed(now)) ticker ??= setInterval(tick, 1_000);
  };
  /** Keep the clock ticking until `untilMs` (a countdown on screen ends then); idempotent. */
  const tickUntil = (untilMs: number | null): void => {
    if (disposed || untilMs === null || Number.isNaN(untilMs)) return;
    if (untilMs <= needUntil) return;
    needUntil = untilMs;
    if (untilMs > Date.now()) ticker ??= setInterval(tick, 1_000);
  };
  const putWait = (key: string, w: MoveWait): void => {
    setWaits((m) => new Map(m).set(key, w));
    retick();
  };
  onCleanup(() => {
    disposed = true;
    if (ticker !== undefined) clearInterval(ticker);
    ticker = undefined;
    // A preview still pending is invalidated: its answer is dropped (`gone`), and nothing
    // reads this session again.
    setSession(null);
    setPressed(null);
  });
  return {
    session,
    setSession,
    pressed,
    setPressed,
    over,
    setOver,
    waits,
    setWaits,
    putWait,
    retick,
    tickUntil,
    /** True once the page left: every late answer checks it before touching anything. */
    gone: (): boolean => disposed,
    live,
    setLive,
    clock,
    focusKey,
    setFocusKey,
    flash,
    setFlash,
  };
}

export type MoveState = ReturnType<typeof createMoveState>;

/** Names, the announcer and the one sentence renderer, over this read of the fleet. */
function moveWords(st: MoveState, deps: MovePageDeps) {
  // One index per fleet read (#6772): every grip, menu and meter on up to 60 cards asks by id.
  // Keyed by the read's array, not a memo: reading `deps.boxes()` here still tracks, and an
  // accessor that is a plain function (a test rig) is read afresh on every call.
  let index: { readonly of: readonly UtilBox[]; readonly byId: Map<string, UtilBox> } | null = null;
  const boxOf = (id: string | null): UtilBox | undefined => {
    if (id === null) return undefined;
    const all = deps.boxes();
    if (index?.of !== all) index = { of: all, byId: new Map(all.map((b) => [b.id, b])) };
    return index.byId.get(id);
  };
  const announce = (text: string): void => {
    if (st.gone()) return;
    // Cleared first, so the same sentence twice is read twice.
    st.setLive('');
    queueMicrotask(() => {
      if (!st.gone()) st.setLive(text);
    });
  };
  /** What a sentence reads beyond the refusal: names, the item's role, the target's CPU. */
  const copyContext = (targetCpuAfter: number | null): MoveCopyContext => ({
    machineName: (id) => boxOf(id)?.display_name ?? null,
    roleOfItem: (item) => {
      if (item === null) return null;
      for (const b of deps.boxes()) {
        const job = (b.jobs ?? []).find((j) => j.id === item);
        if (job !== undefined) return t(capabilityKey(job.role));
      }
      return null;
    },
    targetCpuAfter,
    nowMs: st.clock(),
  });
  const textOf = (r: MoveRefusalWire, cpuAfter: number | null = null): string =>
    moveRefusalText(r, copyContext(cpuAfter), t);
  return { boxOf, announce, textOf };
}

export type MoveWords = ReturnType<typeof moveWords>;

/** A 201's budgets, one answer per machine. */
function acceptedBudgets(w: MoveWords, req: MoveRequest, a: MoveAccepted): BudgetAnswer[] {
  if (a.budget === undefined) return [];
  const { source, target } = a.budget;
  return [
    { boxId: req.sourceId, ...source, row: w.boxOf(req.sourceId)?.moves },
    { boxId: req.targetId, ...target, row: w.boxOf(req.targetId)?.moves },
  ];
}

/** A refusal body's budget: the machine it names and its `left`, when it carries one. */
function refusalBudgets(w: MoveWords, r: MoveRefusalWire | null): BudgetAnswer[] {
  if (r === null || r.machine_id === undefined || r.left === undefined) return [];
  return [
    {
      boxId: r.machine_id,
      left: r.left,
      free_at: r.free_at ?? null,
      row: w.boxOf(r.machine_id)?.moves,
    },
  ];
}

/** The single item's request, as #6771's drag and Move to send it. */
const subjectRequest = (subject: MoveSubject, targetId: string): MoveRequest => ({
  sourceId: subject.sourceId,
  items: [subject.item],
  targetId,
  key: subject.key,
  what: subject.what,
  many: subject.many,
  zone: subject.item.kind === 'task' ? 'now' : 'mission',
  subject,
});

/**
 * The one session's preview asks: start a session, ask its preview again, and forget it. Each
 * answer carries the order its ask started at and the fleet read's item it was for; a newer
 * ask, or a closed session, drops an older answer (`asked`).
 */
function sessionAsks(
  st: MoveState,
  w: MoveWords,
  ask: (sourceId: string, items: readonly MoveItem[]) => Promise<PreviewState>,
  fleetRead: () => number,
) {
  /** Which preview answer is current: a stale one (another session started since) is dropped. */
  let asked = 0;
  const sigOf = (subject: MoveSubject): string => {
    const b = w.boxOf(subject.sourceId);
    return b === undefined ? 'gone' : itemSignature(b, subject.item);
  };
  /** Start a session: ONE preview call for its item. */
  const start = async (subject: MoveSubject, via: MoveSession['via']): Promise<PreviewState> => {
    asked += 1;
    const mine = asked;
    const order = orderStamp();
    const sig = untrack(() => sigOf(subject));
    const read = untrack(fleetRead);
    st.setSession({ subject, via, preview: { status: 'pending' }, order, sig, read });
    const state = await ask(subject.sourceId, [subject.item]);
    if (mine !== asked || st.gone()) return state;
    st.setSession((s) => (s === null ? s : { ...s, preview: state }));
    st.retick();
    if (state.status === 'failed') w.announce(t('fleet.move.preview_failed'));
    return state;
  };
  /**
   * Ask the open session's preview again (a refused pick, or a fleet read that changed its
   * item, #6772 review round 8). The answer on screen stays until the new one lands, and is
   * then replaced whole, with the new ask's order and signature.
   */
  // The ask generation a refresh is in flight for, or null. Scoped to its generation (#6772
  // review round 9): a start or a forget moves `asked` on, so a refresh whose answer is then
  // dropped no longer counts as in flight, and never latches later sessions.
  let refreshing: number | null = null;
  const refresh = async (): Promise<void> => {
    const s = untrack(st.session);
    if (s === null || s.preview.status === 'pending') return;
    asked += 1;
    const mine = asked;
    const order = orderStamp();
    const sig = untrack(() => sigOf(s.subject));
    const read = untrack(fleetRead);
    refreshing = mine;
    const state = await ask(s.subject.sourceId, [s.subject.item]);
    if (refreshing === mine) refreshing = null;
    if (mine !== asked || st.gone()) return;
    st.setSession((cur) =>
      cur === null || cur.subject.key !== s.subject.key
        ? cur
        : { ...cur, preview: state, order, sig, read },
    );
    st.retick();
  };
  // An open session is asked again when a newer fleet read changed its item, or, if its answer
  // holds a refusal no row field clears (`READ_REASKED`), on any newer fleet read at all.
  createEffect(() => {
    const s = st.session();
    if (s === null || s.preview.status === 'pending' || refreshing === asked) return;
    const changed = sigOf(s.subject) !== s.sig;
    const reread = fleetRead() > s.read && previewReaskOnRead(s.preview, untrack(st.clock));
    if (changed || reread) void untrack(refresh);
  });
  /** The session closed (menu shut, drag ended): its preview's answer is no longer wanted. */
  const forget = (): void => {
    asked += 1;
  };
  return { start, refresh, forget };
}

/**
 * A 429 wait whose refusal no fleet row field clears (`transfers_in_flight`, `READ_REASKED`) is
 * asked again on each newer fleet read; a preview that confirms its machine supersedes it before
 * its `free_at`. The other waits (`cooling_down`, the two budgets) end exactly at the server's
 * `free_at`, which no read can bring forward.
 */
function rereadWaits(
  st: MoveState,
  ask: (sourceId: string, items: readonly MoveItem[]) => Promise<PreviewState>,
  fleetRead: () => number,
) {
  createEffect(() => {
    const read = fleetRead();
    for (const [key, wt] of untrack(st.waits)) {
      if (read <= wt.read || !reaskOnRead(wt.refusal, Date.now())) continue;
      void ask(wt.sourceId, wt.items).then((state) => {
        if (st.gone() || state.status !== 'ready') return;
        if (!previewConfirms(state.preview, wt.targetId, Date.now())) return;
        st.setWaits((m) => (m.get(key) === wt ? without(m, key) : m));
      });
    }
  });
}

/** The two doors: the session's ONE preview, and the move through the page guard. */
function moveDoors(st: MoveState, deps: MovePageDeps, w: MoveWords, budgets: MoveBudgets) {
  const previewer = deps.previewer ?? previewMove;
  const mover = deps.mover ?? sendMove;
  const mintKey = deps.mintKey ?? (() => crypto.randomUUID());
  const reportError =
    deps.reportError ?? ((err: unknown) => reportClientError(clientErrorReport('error', err)));
  /** One preview call; its answer's source budget is laid over the machine's row. */
  const ask = async (sourceId: string, items: readonly MoveItem[]): Promise<PreviewState> => {
    const ticket = budgets.previewStarted(sourceId);
    try {
      const preview = await previewer(sourceId, items, deps.token());
      const { left, free_at } = preview.source;
      budgets.previewAnswered(ticket, {
        boxId: sourceId,
        left,
        free_at,
        row: w.boxOf(sourceId)?.moves,
      });
      return { status: 'ready', preview };
    } catch {
      return { status: 'failed' };
    }
  };
  // Each fleet read the page shows, counted: a new `readOrder` or a new boxes array is one.
  let reads = 0;
  const fleetRead = createMemo(() => {
    deps.readOrder?.();
    deps.boxes();
    reads += 1;
    return reads;
  });
  const session = sessionAsks(st, w, ask, fleetRead);
  rereadWaits(st, ask, fleetRead);
  const send = async (req: MoveRequest): Promise<MoveOutcome> => {
    // When this send STARTED, before the POST: the transfer card's order (#6773).
    const order = orderStamp();
    const name = w.boxOf(req.targetId)?.display_name ?? req.targetId.slice(0, 8);
    // Settles once, whatever happens below (`MutationTicket`): never twice, never not at all.
    const ticket = budgets.mutationSent([req.sourceId, req.targetId]);
    try {
      let accepted: MoveAccepted;
      // Only the POST itself can refuse or fail the move. Everything after a 201 is the page
      // reacting to a success, and a throw there never turns it into a failure.
      try {
        accepted = await mover(
          req.sourceId,
          req.items,
          req.targetId,
          mintKey(),
          deps.token(),
          req.undoOf,
        );
      } catch (err) {
        // The page left while the move was in flight: nothing here may start a wait or speak.
        if (st.gone()) return { kind: 'failed' };
        const refusal = readMoveRefusal(err);
        ticket.settle(refusalBudgets(w, refusal));
        const at = { read: untrack(fleetRead), order, report: reportError };
        return refuseSend(st, deps, w, at, req, refusal);
      }
      if (st.gone()) return { kind: 'moved', accepted };
      st.setWaits((m) => without(m, req.key));
      ticket.settle(acceptedBudgets(w, req, accepted));
      const first = req.items[0];
      if (first !== undefined) st.setFocusKey(`${req.targetId}|${first.kind}:${first.id}`);
      // The hook runs NOW, on the answer itself and before the re-read below, while the moved
      // rows are still on their source card (the transfer motion starts from them). Its own
      // error is reported as its own, apart from the move (#6772 review round 11).
      try {
        deps.onMoveAccepted?.({ request: req, accepted }, order);
      } catch (err) {
        reportError(err);
      }
      w.announce(
        req.announce ??
          t(req.many ? 'fleet.move.announce_moving_many' : 'fleet.move.announce_moving', {
            what: req.what,
            name,
          }),
      );
      return { kind: 'moved', accepted };
    } finally {
      ticket.settle([]);
      if (!st.gone()) deps.onWritten();
    }
  };
  /**
   * A move: ONE send through the page's flood guard, with no debounce. Resolves with how it
   * ended; a guard that holds it (locked, or this key still saving) sends nothing.
   */
  const sendGuarded = (req: MoveRequest): Promise<MoveOutcome> =>
    new Promise((resolve) => {
      const verdict = deps.guard.guard.change(`move:${req.key}`, {
        from: req.sourceId,
        to: req.targetId,
        delayMs: 0,
        send: async () => resolve(await send(req)),
        onDropped: () => {
          w.announce(t('fleet.move.grip_why_locked'));
          resolve({ kind: 'held' });
        },
      });
      if (verdict === 'locked') w.announce(t('fleet.move.grip_why_locked'));
      if (verdict !== 'queued') resolve({ kind: 'held' });
    });
  return {
    fleetRead,
    /** A drag refused at its source or on a drop target, handed to the refusal hook (#6773). */
    dragRefused(
      subject: MoveSubject,
      targetId: string | null,
      refusal: MoveRefusalWire,
      order: number,
    ): void {
      if (st.gone()) return;
      const r = { sourceId: subject.sourceId, targetId, subject, what: subject.what };
      notifyRefused(deps, reportError, { ...r, putBack: false, refusal, order });
    },
    start: session.start,
    refresh: session.refresh,
    move: (subject: MoveSubject, targetId: string): Promise<MoveOutcome> =>
      sendGuarded(subjectRequest(subject, targetId)),
    moveMany: sendGuarded,
    ask,
    forget: session.forget,
  };
}

/** What every card reads of the one session: the menu, the drag, each target's answer. */
function sessionReaders(st: MoveState, w: MoveWords) {
  const sourceRefusal = (): MoveRefusalWire | null => {
    const s = st.session();
    if (s === null || s.preview.status !== 'ready') return null;
    return standingRefusal(s.preview.preview.source.refusal, st.clock());
  };
  return {
    /** Move to's open item, or null. */
    menuKey: (): string | null => {
      const s = st.session();
      return s !== null && s.via === 'menu' ? s.subject.key : null;
    },
    /** The drag's item, or null. */
    dragging: (): MoveSubject | null => {
      const s = st.session();
      return s !== null && s.via === 'drag' ? s.subject : null;
    },
    /** What `targetId` says in this session; null when the preview names no such target. */
    targetView(targetId: string): TargetView | null {
      const s = st.session();
      if (s === null) return null;
      if (s.preview.status === 'pending') return { kind: 'checking' };
      if (s.preview.status === 'failed') return null;
      const target = s.preview.preview.targets.find((x) => x.runner_id === targetId);
      if (target === undefined) return null;
      // A timed refusal whose wait has passed no longer stands: the card takes the drop again.
      const now = st.clock();
      const r =
        standingRefusal(s.preview.preview.source.refusal, now) ??
        standingRefusal(target.refusal, now);
      return {
        kind: 'answer',
        target,
        why: r === null ? null : w.textOf(r, target.cpu?.after ?? null),
        refusal: r,
      };
    },
    /** The source's own standing refusal in this session; null when it may move. */
    sourceRefusal,
    /** The source's own refusal in this session, translated; null when it may move. */
    sourceWhy(): string | null {
      const r = sourceRefusal();
      return r === null ? null : w.textOf(r);
    },
    /** The 429 still waiting on this item, its sentence counting down. */
    waitFor(key: string): (MoveWait & { readonly text: string }) | null {
      const wait = st.waits().get(key);
      if (wait === undefined || wait.until <= st.clock()) return null;
      return { ...wait, text: w.textOf(wait.refusal) };
    },
  };
}

/** Which card's Move work panel is open (#6772): one per page, as Send work's form. */
export type WorkPanel = {
  readonly boxId: string;
  /** Opened through Drain machine: everything movable ticked. */
  readonly drain: boolean;
  /** Each open is a new instance, so a reopen starts the panel afresh. */
  readonly n: number;
};

type MoveDoors = ReturnType<typeof moveDoors>;

/** #6772's members: the multi-item send, the panel, the budgets and the deadline registry. */
function workMembers(
  st: MoveState,
  deps: MovePageDeps,
  w: MoveWords,
  doors: MoveDoors,
  budgets: MoveBudgets,
) {
  const [panel, setPanel] = createSignal<WorkPanel | null>(null);
  let opened = 0;
  const [rendered, setRendered] = createSignal<ReadonlySet<string>>(new Set());
  /** A machine's moves: its row, with a newer answer laid over it; undefined without a row. */
  const movesOf = (boxId: string): MovesRow | undefined => {
    const row = w.boxOf(boxId)?.moves;
    const laid = budgets.laidOf(boxId);
    // No answer laid over this machine: its row (so a tick or an answer for another machine
    // re-runs none of its readers).
    if (laid === undefined) return row ?? undefined;
    // A fleet read that overlaps a mutation in flight on this machine may not show it: the answer
    // laid over it stands. Otherwise a read that started after the answer replaces it; without a
    // read time, a new row object IS that newer read.
    const readOrder = budgets.busy(boxId)
      ? Number.NEGATIVE_INFINITY
      : (deps.readOrder?.() ??
        (laid.row !== row ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY));
    return effectiveMoves(row, laid.overlay, readOrder);
  };
  // A passed `free_at` is settled by a fresh read of the fleet, never by reverting to the row.
  createEffect(() => {
    st.clock();
    untrack(() => budgets.expire(Date.now()));
  });
  /** This org's own machines on the page (Move work needs another). */
  let own: { readonly of: readonly UtilBox[]; readonly n: number } | null = null;
  const ownMachines = (): number => {
    const all = deps.boxes();
    if (own?.of !== all) own = { of: all, n: all.filter((b) => b.via_org === null).length };
    return own.n;
  };
  return {
    /** The whole fleet read this page moves within. */
    boxes: deps.boxes,
    ownMachines,
    /** Several items off one machine in ONE send through the page guard (#6772). */
    moveMany: doors.moveMany,
    /**
     * A preview for `items` off `sourceId` that leaves the one session alone (the Move work
     * panel's per-item and selection previews). Its answer's budget is laid over the row.
     */
    previewItems: (sourceId: string, items: readonly MoveItem[]): Promise<PreviewState> =>
      doors.ask(sourceId, items),
    movesOf,
    /** Keep the page clock ticking until `untilMs`: a countdown on screen ends then. */
    tickUntil: st.tickUntil,
    /** Read the clock now: an answer just landed, and its times count from this moment. */
    readClock: st.retick,
    panel,
    /** Open Move work on this card, closing Move to (one panel, one menu, per page). */
    openPanel(boxId: string, drain: boolean): void {
      if (untrack(st.session)?.via === 'menu') {
        doors.forget();
        st.setSession(null);
      }
      opened += 1;
      setPanel({ boxId, drain, n: opened });
    },
    closePanel(): void {
      setPanel(null);
    },
    /** The machines whose cards are rendered now (a note's "on screen", #6772). */
    rendered,
    /** A card mounted; the returned function is its unmount. */
    renderCard(boxId: string): () => void {
      setRendered((r) => new Set(r).add(boxId));
      return () =>
        setRendered((r) => {
          const next = new Set(r);
          next.delete(boxId);
          return next;
        });
    },
    /**
     * Why a move off this machine is refused before anything is sent, translated, or null:
     * its own move budget read 0 (the row, or a newer answer), or the page is locked. The ONE
     * check every entry point reads (the press, the grip, the Move button, a Move to pick, a
     * drop, the panel's Move, Drain, Undo, each Put-back part); tracked.
     */
    blockedWhy: (sourceId: string): string | null => {
      const moves = movesOf(sourceId);
      return gripDisabledWhy(moves === undefined ? {} : { moves }, lockedSeconds(deps.guard), t);
    },
  };
}

export function createMovePage(deps: MovePageDeps) {
  const st = createMoveState();
  const w = moveWords(st, deps);
  const budgets = createMoveBudgets({
    gone: st.gone,
    tickUntil: st.tickUntil,
    reread: () => deps.onWritten(),
  });
  const doors = moveDoors(st, deps, w, budgets);
  return {
    ...w,
    ...sessionReaders(st, w),
    ...workMembers(st, deps, w, doors, budgets),
    move: doors.move,
    dragRefused: doors.dragRefused,
    /** Ask the open session's preview again; the current answer shows until it lands. */
    refreshSession: doors.refresh,
    /** Counts the fleet reads the page has shown: an answer asked on an older one may be re-asked. */
    fleetRead: doors.fleetRead,
    session: st.session,
    live: st.live,
    /** The page's ticking clock: every countdown and every timed refusal reads it. */
    clock: st.clock,
    /** Seconds until the page guard unlocks; tracked. */
    locked: (): number => lockedSeconds(deps.guard),
    /** Open Move to for an item, closing any other drag or menu; a second open closes it. */
    openMenu(subject: MoveSubject): void {
      if (untrack(st.session)?.subject.key === subject.key) {
        doors.forget();
        st.setSession(null);
        return;
      }
      void doors.start(subject, 'menu');
    },
    closeMenu(): void {
      if (untrack(st.session)?.via !== 'menu') return;
      doors.forget();
      st.setSession(null);
    },
    /** A drag crossed its 7 px: start the session; resolves with the preview's answer. */
    beginDrag: (subject: MoveSubject): Promise<PreviewState> => doors.start(subject, 'drag'),
    /** The drag ended (dropped, cancelled or refused at its source). */
    endDrag(): void {
      st.setOver(null);
      if (untrack(st.session)?.via !== 'drag') return;
      doors.forget();
      st.setSession(null);
    },
    pressed: st.pressed,
    /** A row was pressed where a drag may start. */
    press: (p: MovePress): void => {
      st.setPressed(p);
    },
    release: (): void => {
      st.setPressed(null);
    },
    /** The card under the pointer and the pointer's offset in it; null over none. */
    over: st.over,
    setOver: (id: string | null, y = 0): void => {
      st.setOver(id === null ? null : { id, y });
    },
    /** The option flashing after a pick, `<item key>><target id>`. */
    flash: st.flash,
    setFlash: st.setFlash,
    /** The moved row's key on its new card, focused once it shows; taken by that row. */
    focusKey: st.focusKey,
    takeFocus(key: string): boolean {
      if (untrack(st.focusKey) !== key) return false;
      st.setFocusKey(null);
      return true;
    },
  };
}

export type MovePage = ReturnType<typeof createMovePage>;

export const MovePageContext = createContext<MovePage | null>(null);

/** The page's move controller, or null outside a Fleet page (then no card offers a move). */
export function useMovePage(): MovePage | null {
  return useContext(MovePageContext) ?? null;
}
